<template>
  <div class="reports-container">
    <!-- Header -->
    <div class="text-center space-y-2 mb-6">
      <h1 class="text-3xl font-semibold text-foreground">학습 보고서</h1>
      <p class="text-muted-foreground">
        AITRA 플랫폼에서 진행된 강의들의 요약과 핵심 내용
      </p>
    </div>

    <!-- Filter Section -->
    <div class="filter-card">
      <div class="filter-content">
        <div class="flex items-center gap-2 text-sm text-muted-foreground mb-4">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span>조회 조건</span>
      </div>
      
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <!-- Search Input -->
          <div class="relative">
            <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
        <input 
          v-model="searchQuery" 
              placeholder="키워드 검색..."
              class="search-input pl-10"
          @input="watchSearchQuery"
            />
          </div>

          <!-- Category Select -->
          <select v-model="selectedCategory" class="filter-select">
            <option value="all">전체 키워드</option>
            <option v-for="category in categories.slice(1)" :key="category" :value="category">
              {{ category }}
            </option>
          </select>

          <!-- Period Select -->
          <select v-model="selectedPeriod" class="filter-select">
            <option v-for="period in periods" :key="period" :value="period">
              {{ period === "all" ? "전체 기간" : period }}
            </option>
        </select>
      </div>

        <div class="text-sm text-primary mt-4">
          총 {{ filteredReports.length }}개의 결과
        </div>
      </div>
    </div>

    <!-- View Mode Toggle -->
    <div class="flex justify-end gap-2 mb-6">
      <button
        :class="['view-toggle-btn', viewMode === 'card' ? 'active' : '']"
        @click="viewMode = 'card'"
      >
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
        카드형
      </button>
      <button
        :class="['view-toggle-btn', viewMode === 'list' ? 'active' : '']"
        @click="viewMode = 'list'"
      >
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
        목록형
      </button>
    </div>

    <!-- Reports Display -->
    <div v-if="viewMode === 'card'" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div 
          v-for="report in filteredReports" 
          :key="report.id" 
          class="report-card"
          @click="selectReport(report)"
        >
        <div class="card-header">
          <div class="category-section">
            <span 
              class="category-badge"
              :style="{ backgroundColor: report.categoryColor }"
            >
              {{ report.category }}
            </span>
            <span class="views-count">{{ report.views }}회 읽음</span>
          </div>
        </div>
        <div class="card-content">
          <h3 class="card-title">{{ report.title }}</h3>
          
          <p class="card-description">
            {{ report.description }}
          </p>

          <div class="tags">
            <span 
              v-for="(tag, idx) in report.tags" 
              :key="idx" 
              class="tag"
            >
              {{ tag }}
            </span>
          </div>

          <div class="card-footer">
            <div class="footer-item">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>{{ report.author }}</span>
        </div>
            <div class="footer-item">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{{ report.date }}</span>
            </div>
            <div class="footer-item">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{{ report.duration }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- List View -->
    <div v-else class="list-card">
      <div class="list-content">
        <div class="list-item" 
          v-for="report in filteredReports" 
          :key="report.id"
          @click="selectReport(report)"
        >
          <div class="list-main">
            <span 
              class="category-badge"
              :style="{ backgroundColor: report.categoryColor }"
            >
              {{ report.category }}
            </span>
            <h3 class="list-title">{{ report.title }}</h3>
          </div>
          
          <div class="list-meta">
            <div class="meta-item">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>{{ report.author }}</span>
            </div>
            <div class="meta-item">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{{ report.date }}</span>
            </div>
            <div class="meta-item">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{{ report.duration }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
        <div class="pagination">
          <button 
            class="pagination-button" 
            :disabled="currentPage === 0"
            @click="changePage(currentPage - 1)"
          >
            이전
          </button>
          
          <span class="pagination-info">
            {{ currentPage + 1 }} / {{ totalPages }} 페이지
            (총 {{ totalElements }}건)
          </span>
          
          <button 
            class="pagination-button"
            :disabled="currentPage >= totalPages - 1"
            @click="changePage(currentPage + 1)"
          >
            다음
          </button>
    </div>

    <!-- 보고서 상세 모달 -->
    <div v-if="showReportModal && modalReport" class="report-modal-overlay" @click="closeReportModal">
      <div class="report-modal" @click.stop>
        <button class="modal-close" @click="closeReportModal">×</button>
        <div class="modal-content">
          <h2 class="modal-title">{{ modalReport.title || '제목 없음' }}</h2>
          <div class="modal-info">
            <p><span class="icon">📅</span> {{ modalReport.date || '' }} {{ modalReport.time || '' }}</p>
            <p><span class="icon">👤</span> 주관: {{ modalReport.host || '정보 없음' }}</p>
            <p><span class="icon">👥</span> 참석자: {{ modalReport.participants && Array.isArray(modalReport.participants) && modalReport.participants.length > 0 ? modalReport.participants.join(', ') : '정보 없음' }}</p>
          </div>
          <div class="modal-body">
            <ReportSummaryRenderer :markdown-content="modalReport.content || ''" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { useRouter } from 'vue-router'
import ReportSummaryRenderer from '@/components/ReportSummaryRenderer.vue'
import { restApiClient } from '@/api/apiConfig'
import { ReportParser } from '@/utils/reportParser'

const router = useRouter()
const { isAuthenticated, user } = useAuth()

// 상태 관리
const searchQuery = ref('')
const selectedCategory = ref('all')
const selectedPeriod = ref('all')
const viewMode = ref('card') // 'card' or 'list'
const modalReport = ref(null)     // 모달 표시용
const showReportModal = ref(false)

// 카테고리와 기간 옵션
const categories = ref(['all']) // 동적으로 업데이트됨 (보고서 데이터에서 keywords 추출)
const periods = ['all', '오늘', '이번 주', '이번 달']

// 한글 기간 값을 백엔드 형식으로 변환하는 함수
const convertPeriodToBackendFormat = (period) => {
  const periodMap = {
    'all': 'all',
    '오늘': 'today',
    '이번 주': 'week',
    '이번 달': 'month',
  };
  return periodMap[period] || 'all';
}

// ReportParser 인스턴스 생성
const reportParser = new ReportParser();

// 보고서 데이터에서 고유한 키워드를 추출하여 카테고리 목록 업데이트
// 기존 카테고리에 새로운 키워드를 추가하는 누적 방식
const updateCategoriesFromReports = (reportsData) => {
  if (!reportsData || !Array.isArray(reportsData)) {
    return;
  }
  
  // 기존 카테고리 목록을 Set으로 변환 ('all' 제외)
  const existingKeywords = new Set(
    categories.value.filter(cat => cat !== 'all')
  );
  
  // 새로운 보고서에서 keywords/tags 수집
  reportsData.forEach(report => {
    // 1. content에서 keywords 파싱 (가장 우선순위 높음)
    if (report.content) {
      try {
        const parsedResult = reportParser.parse(report.content);
        if (parsedResult.isStandardReport && parsedResult.keywords && Array.isArray(parsedResult.keywords)) {
          parsedResult.keywords.forEach(keyword => {
            if (keyword && keyword.trim()) {
              existingKeywords.add(keyword.trim());
            }
          });
        }
      } catch (error) {
        console.warn('Content 파싱 중 오류:', error);
      }
    }
    
    // 2. tags 배열이 있으면 사용
    if (report.tags && Array.isArray(report.tags)) {
      report.tags.forEach(tag => {
        if (tag && tag.trim()) {
          existingKeywords.add(tag.trim());
        }
      });
    }
    
    // 3. keywords 필드가 있으면 사용
    if (report.keywords && Array.isArray(report.keywords)) {
      report.keywords.forEach(keyword => {
        if (keyword && keyword.trim()) {
          existingKeywords.add(keyword.trim());
        }
      });
    }
    
    // 4. category 필드가 있으면 사용
    if (report.category && report.category !== '기타') {
      existingKeywords.add(report.category.trim());
    }
  });
  
  // 'all'을 맨 앞에 추가하고 나머지는 정렬
  const sortedKeywords = Array.from(existingKeywords).sort();
  categories.value = ['all', ...sortedKeywords];
}

// API 호출 관련 상태 관리
const isLoading = ref(false)
const error = ref(null)
const reports = ref([]) // 보고서 목록

// 페이지네이션 관련 상태 추가
const currentPage = ref(0)
const pageSize = ref(6)
const totalPages = ref(0)
const totalElements = ref(0)

// Debounce를 위한 타이머
let searchDebounceTimer = null

// 캐시 관리 클래스
class CacheManager {
  constructor() {
    this.cache = new Map();
    this.maxSize = 50; // 최대 50개 항목 유지
  }

  // 캐시 키 생성
  getKey(page, size, filterDate, sortBy, keyword = '') {
    return `${page}-${size}-${filterDate}-${sortBy}-${keyword}`;
  }

  // 캐시에서 데이터 가져오기
  getCache(key) {
    return this.cache.get(key);
  }

  // 캐시에 데이터 저장
  setCache(key, data) {
    // 캐시 크기 제한
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    this.cache.set(key, data);
  }

  // 캐시 초기화
  clearCache() {
    this.cache.clear();
  }
}

// 캐시 매니저 인스턴스
const cacheManager = new CacheManager();

// 전체 결과 캐시 관리 클래스
class FullDataCache {
  constructor() {
    this.allItems = new Map(); // 키: cacheKey, 값: 전체 결과
  }

  // 전체 결과 저장
  setItems(cacheKey, fullResponse) {
    this.allItems[cacheKey] = fullResponse;
  }

  // 특정 페이지의 데이터 추출
  getPageItems(cacheKey, page, pageSize) {
    const fullData = this.allItems[cacheKey];
    if (!fullData || !fullData.content) {
      return { content: [], totalPages: 0, totalElements: 0 };
    }

    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;
    const pageContent = fullData.content.slice(startIndex, endIndex);
    
    return {
      content: pageContent,
      totalPages: Math.ceil(fullData.content.length / pageSize),
      totalElements: fullData.content.length
    };
  }
  
  // 특정 키의 데이터 존재 여부 확인
  hasKey(cacheKey) {
    return !!this.allItems[cacheKey];
  }
}

// 전체 결과 캐시 인스턴스
const cachedFullData = new FullDataCache();

// 보고서 목록 조회 API
const fetchReports = async () => {
  if (!isAuthenticated.value) {
    console.error('로그인이 필요합니다.');
    router.push('/login');
    return;
  }
  const userId = user.value?.id;
  if (!userId) {
    console.error('사용자 정보를 찾을 수 없습니다.');
    return;
  }
  
  // 캐시 키 생성
  const cacheKey = cacheManager.getKey(
    currentPage.value,
    pageSize.value,
    selectedPeriod.value,
    'date'
  );
  
    // 캐시 확인
    const cachedData = cacheManager.getCache(cacheKey);
    if (cachedData) {
    reports.value = cachedData.content.map(report => ({
      id: report.id,
      title: report.title,
      description: report.description || '',
      category: report.category || '기타',
      categoryColor: getCategoryColor(report.category),
      tags: report.tags || [],
      author: report.host || '작성자 정보 없음',
      date: report.date || '날짜 없음',
      duration: report.duration || '',
      views: report.views || 0,
      // 모달에 필요한 추가 정보
      content: report.content || '',
      participants: report.participants || [],
      createdAt: report.createdAt,
      agenda: report.agenda || ''
    }));
    // 카테고리 목록 업데이트
    updateCategoriesFromReports(reports.value);
    totalPages.value = cachedData.totalPages;
    totalElements.value = cachedData.totalElements;
    return;
  }
  
  try {
    isLoading.value = true;
    error.value = null; // 에러 상태 초기화
    const response = await restApiClient.get('/restapi/reports/paged', {
      params: {
        page: currentPage.value,
        size: pageSize.value,
        sortBy: 'createdAt',
        direction: 'desc',
        dateFilter: convertPeriodToBackendFormat(selectedPeriod.value),
        userId: userId
      }
    });
    
    // 페이지 정보 업데이트
    totalPages.value = response?.totalPages || 0;
    totalElements.value = response?.totalElements || 0;
    
    // 받아온 데이터 매핑
    reports.value = response?.content?.map(report => ({
        id: report.id,
        title: report.title,
      description: report.description || '',
      category: report.category || '기타',
      categoryColor: getCategoryColor(report.category),
      tags: report.tags || [],
      author: report.host || '작성자 정보 없음',
        date: report.date || '날짜 없음',
      duration: report.duration || '',
      views: report.views || 0,
      // 모달에 필요한 추가 정보
      content: report.content || '',
      participants: report.participants || [],
      createdAt: report.createdAt,
      agenda: report.agenda || ''
    })) || [];
    
    // 카테고리 목록 업데이트 (모든 보고서의 keywords 수집)
    updateCategoriesFromReports(reports.value);
    
    // 결과 캐싱
    cacheManager.setCache(cacheKey, response);
  } catch (err) {
    error.value = err.message;
    console.error('보고서 목록을 불러오는데 실패했습니다:', err);
  } finally {
    isLoading.value = false;
  }
};

// 키워드로 보고서 검색 API
const searchReportsByKeyword = async (keyword) => {
  if (!isAuthenticated.value) {
    console.error('로그인이 필요합니다.');
    router.push('/login');
    return;
  }
  const userId = user.value?.id;
  if (!userId) {
    console.error('사용자 정보를 찾을 수 없습니다.');
    return;
  }
  
  // 검색어 유효성 검사 추가
  if (!keyword || !keyword.trim()) {
    error.value = null; // 에러 상태 초기화
    return await fetchReports(); // 빈 검색어는 기본 목록 로드
  }
  
  // 캐시 키 생성 (페이지 번호 제외)
  const cacheKey = cacheManager.getKey(
    selectedPeriod.value,
    'date',
    keyword.trim()
  );
  
  // 캐시 확인
  const cachedData = cacheManager.getCache(cacheKey);
  if (cachedData) {
    // 전체 결과 캐시 객체에 저장
    if (!cachedFullData.hasKey(cacheKey)) {
      cachedFullData.setItems(cacheKey, cachedData);
    }
    
    // 현재 페이지에 해당하는 데이터만 추출
    const pageData = cachedFullData.getPageItems(cacheKey, currentPage.value, pageSize.value);
    
    reports.value = pageData.content.map(report => ({
      id: report.id,
      title: report.title,
      description: report.description || '',
      category: report.category || '기타',
      categoryColor: getCategoryColor(report.category),
      tags: report.tags || [],
      author: report.host || '작성자 정보 없음',
      date: report.date || '날짜 없음',
      duration: report.duration || '',
      views: report.views || 0,
      // 모달에 필요한 추가 정보
      content: report.content || '',
      participants: report.participants || [],
      createdAt: report.createdAt,
      agenda: report.agenda || ''
    }));
    // 카테고리 목록 업데이트 (전체 캐시된 데이터에서)
    if (cachedFullData.allItems[cacheKey] && cachedFullData.allItems[cacheKey].content) {
      updateCategoriesFromReports(cachedFullData.allItems[cacheKey].content);
    } else {
      updateCategoriesFromReports(reports.value);
    }
    totalPages.value = pageData.totalPages;
    totalElements.value = pageData.totalElements;
    return;
  }
  
  try {
    isLoading.value = true;
    error.value = null;
    
    const params = {
      keyword: keyword.trim(),
      page: 0, // 무조건 첫 페이지부터
      size: 100, // 더 큰 사이즈로 한 번에 가져옴
      sortBy: 'created_at',
      direction: 'desc',
      dateFilter: convertPeriodToBackendFormat(selectedPeriod.value),
      userId: userId
    };
    
    const response = await restApiClient.get('/restapi/reports/search/paged', { params });
    
    // 검사 강화
    if (response && Array.isArray(response.content)) {
      // 전체 결과 캐시 저장
      cacheManager.setCache(cacheKey, response);
      cachedFullData.setItems(cacheKey, response);
      
      // 현재 페이지 데이터 추출
      const pageData = cachedFullData.getPageItems(cacheKey, currentPage.value, pageSize.value);
      
      reports.value = pageData.content.map(report => ({
        id: report.id,
        title: report.title,
        description: report.description || '',
        category: report.category || '기타',
        categoryColor: getCategoryColor(report.category),
        tags: report.tags || [],
        author: report.host || '작성자 정보 없음',
        date: report.date || '날짜 없음',
        duration: report.duration || '',
        views: report.views || 0,
        // 모달에 필요한 추가 정보
        content: report.content || '',
        participants: report.participants || [],
        createdAt: report.createdAt,
        agenda: report.agenda || ''
      }));
      
      // 카테고리 목록 업데이트 (전체 검색 결과에서)
      updateCategoriesFromReports(response.content);
      
      totalPages.value = pageData.totalPages;
      totalElements.value = pageData.totalElements;
      error.value = null; // 성공 시 에러 상태 초기화
      
    } else {
      console.error('예상치 못한 응답 형식:', response);
      error.value = '서버 응답 형식이 올바르지 않습니다';
      reports.value = [];
    }
  } catch (err) {
    console.error('검색 API 오류:', err);
    // 404 에러는 검색 결과가 없다는 의미이므로 에러로 처리하지 않고 빈 결과로 처리
    if (err.response?.status === 404) {
      error.value = null; // 404는 에러가 아니라 빈 결과임
      reports.value = [];
      totalPages.value = 0;
      totalElements.value = 0;
    } else {
      error.value = `검색 실패: ${err.response?.status === 400 ? '검색어를 입력해주세요' : err.message || '서버 연결 오류'}`;
      reports.value = [];
    }
  } finally {
    isLoading.value = false;
  }
};

// 카테고리별 색상 매핑
const getCategoryColor = (category) => {
  const colorMap = {
    '인공지능': '#3b82f6',
    '경제학': '#2563eb',
    '물리학': '#1d4ed8',
    '생명과학': '#1e40af',
    '컴퓨터과학': '#60a5fa',
    '철학': '#93c5fd',
    '기타': '#6b7280'
  };
  return colorMap[category] || '#6b7280';
};

// 필터링된 보고서 목록
const filteredReports = computed(() => {
  if (isLoading.value) {
    return []; // 로딩 중일 때는 빈 배열 반환
  }
  
  if (error.value) {
    console.error('에러 발생:', error.value);
    return [];
  }
  
  return reports.value.filter(report => {
    if (selectedCategory.value === "all") {
      return true;
    }
    
    // 1. content에서 keywords 파싱하여 확인 (가장 우선순위)
    if (report.content) {
      try {
        const parsedResult = reportParser.parse(report.content);
        if (parsedResult.isStandardReport && parsedResult.keywords && Array.isArray(parsedResult.keywords)) {
          const hasKeywordInContent = parsedResult.keywords.some(keyword => 
            keyword && keyword.trim() === selectedCategory.value
          );
          if (hasKeywordInContent) {
            return true;
          }
        }
      } catch (error) {
        // 파싱 실패 시 무시하고 다음 조건 확인
      }
    }
    
    // 2. tags 배열에 선택된 카테고리가 포함되어 있는지 확인
    if (report.tags && Array.isArray(report.tags) && report.tags.includes(selectedCategory.value)) {
      return true;
    }
    
    // 3. keywords 필드 확인
    if (report.keywords && Array.isArray(report.keywords) && report.keywords.includes(selectedCategory.value)) {
      return true;
    }
    
    // 4. category 필드 확인
    if (report.category && report.category === selectedCategory.value) {
      return true;
    }
    
    return false;
  });
});

// 검색어 변경 감지
function watchSearchQuery() {
  // 이전 타이머 취소
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }
  
  // Debounce: 500ms 후에 검색 실행
  searchDebounceTimer = setTimeout(() => {
    currentPage.value = 0; // 검색 시 첫 페이지로 이동
    if (searchQuery.value.trim()) {
      searchReportsByKeyword(searchQuery.value.trim());
    } else {
      error.value = null; // 에러 상태 초기화
      fetchReports();
    }
  }, 500);
}

// 이벤트 핸들러
function selectReport(report) {
  // 목록에서 받아온 보고서 정보를 모달에 사용
  // content와 participants는 이미 목록 데이터에 포함되어 있음
  modalReport.value = {
    id: report.id,
    title: report.title,
    date: report.date || (report.createdAt ? new Date(report.createdAt).toLocaleDateString('ko-KR') : '날짜 없음'),
    time: report.createdAt ? new Date(report.createdAt).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }) : '',
    host: report.author || report.host || '작성자 정보 없음',
    participants: Array.isArray(report.participants) ? report.participants : [],
    content: report.content || ''
  };
  showReportModal.value = true;
}

// 페이지 변경 함수
function changePage(page) {
  currentPage.value = page;
  
  // 현재 검색어 또는 일반 목록 상태 확인
  const keyword = searchQuery.value.trim();
  const cacheKey = cacheManager.getKey(
    selectedPeriod.value,
    'date',
    keyword
  );
  
  // 캐시된 전체 데이터가 있는지 확인
  if (cachedFullData.hasKey(cacheKey)) {
    // 캐시된 데이터에서 페이지 추출
    const pageData = cachedFullData.getPageItems(cacheKey, currentPage.value, pageSize.value);
    
    // 데이터 설정
    reports.value = pageData.content.map(report => ({
      id: report.id,
      title: report.title,
      description: report.description || '',
      category: report.category || '기타',
      categoryColor: getCategoryColor(report.category),
      tags: report.tags || [],
      author: report.host || '작성자 정보 없음',
      date: report.date || '날짜 없음',
      duration: report.duration || '',
      views: report.views || 0,
      // 모달에 필요한 추가 정보
      content: report.content || '',
      participants: report.participants || [],
      createdAt: report.createdAt,
      agenda: report.agenda || ''
    }));
    
    // 카테고리 목록 업데이트 (전체 캐시된 데이터에서)
    if (cachedFullData.allItems[cacheKey] && cachedFullData.allItems[cacheKey].content) {
      updateCategoriesFromReports(cachedFullData.allItems[cacheKey].content);
    } else {
      updateCategoriesFromReports(reports.value);
    }
    
    totalPages.value = pageData.totalPages;
    totalElements.value = pageData.totalElements;
    return;
  }
  
  // 캐시된 데이터가 없으면 API 호출
  if (keyword) {
    searchReportsByKeyword(keyword);
  } else {
    fetchReports();
  }
}

function closeReportModal() {
  showReportModal.value = false
  modalReport.value = null
}

// 초기 설정
onMounted(async () => {
  if (!isAuthenticated.value) {
    console.log('로그인이 필요합니다. 로그인 페이지로 이동합니다.');
    router.push('/login');
    return;
  }
  await fetchReports();
})

// 기간(selectedPeriod) 변경 시 자동으로 데이터 재조회
watch(selectedPeriod, () => {
  currentPage.value = 0; // 첫 페이지로 리셋
  error.value = null; // 에러 상태 초기화
  
  // 검색어가 있으면 검색 API 호출, 없으면 일반 목록 API 호출
  if (searchQuery.value.trim()) {
    searchReportsByKeyword(searchQuery.value.trim());
  } else {
    fetchReports();
  }
})

// 카테고리(selectedCategory) 변경 시에는 computed로 자동 필터링되므로 API 호출 불필요
// 하지만 필요하다면 watch를 추가할 수 있습니다

// 컴포넌트 언마운트 시 타이머 정리
onBeforeUnmount(() => {
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }
})
</script>

<style scoped>
.reports-container {
  min-height: 100vh;
  background-color: #f0f4f8;
  padding: 1.5rem;
}

/* Filter Card */
.filter-card {
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 1.5rem;
}

.filter-content {
  padding: 1.5rem;
}

/* Search Input */
.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background-color: #f8fafc;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  background-color: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Filter Select */
.filter-select {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background-color: #f8fafc;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1rem;
  padding-right: 2.5rem;
}

.filter-select:focus {
  outline: none;
  border-color: #3b82f6;
  background-color: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* View Toggle Buttons */
.view-toggle-btn {
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background-color: white;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.view-toggle-btn:hover {
  border-color: #3b82f6;
  color: #3b82f6;
}

.view-toggle-btn.active {
  background-color: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

/* Report Cards */
.report-card {
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid #e5e7eb;
}

.report-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  border-color: #3b82f6;
  transform: translateY(-2px);
}

.card-header {
  margin-bottom: 1rem;
  background: transparent;
  border: none;
  box-shadow: none;
}

.category-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
}

.category-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 0.5rem;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
}

.views-count {
  font-size: 0.75rem;
  color: #6b7280;
}

.card-content {
  text-align: center;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1rem;
  line-height: 1.4;
  text-align: center;
}

.card-description {
  font-size: 0.875rem;
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: 1rem;
  text-align: left;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  justify-content: flex-start;
}

.tag {
  font-size: 0.75rem;
  color: #3b82f6;
  font-weight: 500;
}

.card-footer {
  display: flex;
  gap: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f3f4f6;
  justify-content: flex-start;
}

.footer-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: #6b7280;
}

/* List View */
.list-card {
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.list-content {
  padding: 0;
}

.list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background-color 0.2s;
}

.list-item:hover {
  background-color: #f8fafc;
}

.list-item:last-child {
  border-bottom: none;
}

.list-main {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.list-title {
  font-size: 1rem;
  font-weight: 500;
  color: #1f2937;
}

.list-meta {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: #6b7280;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1rem;
}

.pagination-button {
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  font-weight: 500;
}

.pagination-button:hover:not(:disabled) {
  border-color: #3b82f6;
  color: #3b82f6;
}

.pagination-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 0.875rem;
  color: #6b7280;
}

/* Modal Styles */
.report-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
  backdrop-filter: blur(4px);
}

.report-modal {
  background: white;
  border-radius: 0.75rem;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 1.5rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: #6b7280;
}

.modal-close:hover {
  background: #3b82f6;
  color: white;
}

.modal-content {
  padding: 2rem;
  overflow-y: auto;
  max-height: 90vh;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
}

.modal-info {
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-info p {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  color: #6b7280;
}

.modal-body {
  line-height: 1.6;
}

.modal-body :deep(h1) {
  font-size: 1.8rem;
  font-weight: 600;
  margin: 1.5rem 0 1rem;
}

.modal-body :deep(h2) {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1.25rem 0 0.75rem;
}

.modal-body :deep(h3) {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 1rem 0 0.5rem;
}

.modal-body :deep(ul), .modal-body :deep(ol) {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.modal-body :deep(li) {
  margin: 0.25rem 0;
}

.modal-body :deep(p) {
  margin: 0.75rem 0;
}

.modal-body :deep(code) {
  background: #f3f4f6;
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
}

.modal-body :deep(pre) {
  background: #f3f4f6;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  margin: 1rem 0;
}
</style>